import React, { ReactElement } from 'react';
import CatalogImage from 'components/CatalogImage';
import { adjustHiDPI, isVideo } from 'components/helpers/ui';
import { MediaObject } from 'types/media';
import { LivePreviewContainer, LivePreviewContainerInner, TwoColumnContainer } from './styled';
import { VimeoVideoContainer } from 'components/Vimeo/VimeoVideoContainer';
import { generateLivePreviewImage } from '../../features/pdp';
import { LIVE_PREVIEW_MOBILE_INDEX } from 'constants/pdp';

export interface PDPMediaObject extends MediaObject {
  isMobile: boolean;
  zoom?: boolean;
  onClick?: (src: string) => void;
  modalImageZoom?: boolean;
}

interface ReactImageGalleryItem extends MediaObject {
  originalWidth: string;
  originalHeight: string;
  renderItem: (props) => ReactElement;
  isMobile: boolean;
  onClick?: (src: string) => void;
}

export const renderPhoto = (
  pdpMedia: PDPMediaObject,
  onClick?: (src: string) => void
): ReactElement => {
  const { isMobile, full, caption, onClick: mediaOnClick, modalImageZoom } = pdpMedia;

  return (
    <CatalogImage
      key={`pdp-image-${isMobile ? 'mobile-' : 'desktop-' + full}`}
      alt={caption}
      src={full}
      css={{ borderRadius: '2px' }}
      fullscreenOnMobile
      widths={{
        tiny: adjustHiDPI(400), // Small breakpoint is 376px
        small: adjustHiDPI(600), // Medium breakpoint is 576px
        medium: adjustHiDPI(800), // The largest the image will render here is 100vw at 768px breakpoint
        large: adjustHiDPI(1000) // The max width of the GlobalLayout wrapper is set to 1180px, this image will never render larger than half of that
      }}
      zoom={false}
      onClick={onClick || mediaOnClick}
      modalImageZoom={modalImageZoom}
      height="100%"
    />
  );
};

export const renderVideo = (
  pdpMedia: PDPMediaObject,
  onClick?: (src: string) => void
): React.ReactElement => {
  const { videoId, isMobile, videoUrl, onClick: mediaOnClick } = pdpMedia;

  return (
    <VimeoVideoContainer
      key={videoUrl}
      videoId={videoId}
      isMobile={isMobile}
      onClick={() => {
        onClick
          ? onClick(videoUrl)
          : mediaOnClick?.(videoUrl);
      }}
    />
  );
};

const sortPhotos = (photos: MediaObject[]): MediaObject[] =>
  photos.sort((a, b) => {
    if (a.isMain && b.isMain) {
      return 0;
    } else if (a.isMain) {
      return -1;
    } else if (b.isMain) {
      return 1;
    } else if (a.videoUrl === b.videoUrl) {
      return 0;
    } else if (a.videoUrl) {
      return -1;
    } else if (b.videoUrl) {
      return 1;
    }
    return 0;
  });

export const getMobileItems = (
  photos: MediaObject[],
  onClick: (src: string) => void
): ReactImageGalleryItem[] => photos
  .map(photo => ({
    originalWidth: '300px',
    originalHeight: '400px',
    renderItem: isVideo(photo) ? renderVideo : renderPhoto,
    isMobile: true,
    onClick,
    ...photo
  }))
  .filter(Boolean);

export const getDesktopImages = (
  photos: MediaObject[],
  onClick: (src: string) => void
): ReactElement[] => {
  const renderedImages =
    photos
      .map(photo => ({ ...photo, isMobile: false }))
      .map(photo => {
        return isVideo(photo)
          ? renderVideo(photo, onClick)
          : renderPhoto(photo, onClick);
      })
      .filter(Boolean);

  return groupPhotos(renderedImages);
};

const renderedMediaIsVideo = (renderedMedia: ReactElement) => renderedMedia.key && renderedMedia.key.includes('video');

export const groupPhotos = (renderedPhotos: ReactElement[]): ReactElement[] => {
  const arrangedPhotos = renderedPhotos.slice(0, 2).map((photo) => {
    // We need to wrap the live preview in a container so that it
    // doesn't disappear when live preview changes.  Without a container, the image will disappear
    // while the new livepreview image loads.
    if (renderedMediaIsVideo(photo)) {
      return photo;
    }
    return (
      <LivePreviewContainer key={photo.key}>
        <LivePreviewContainerInner>{photo}</LivePreviewContainerInner>
      </LivePreviewContainer>
    );
  }); // First two are unchanged

  for (let i = 2; i < renderedPhotos.length;) {
    // Begin with photo 3 and maybe combine
    let group = null;

    if (arrangedPhotos.length === 3 && renderedPhotos[i]) {
      // First Single after group
      arrangedPhotos.push(renderedPhotos[i]);
      i++;
      continue;
    }

    if (renderedPhotos[i] && renderedPhotos[i + 3]) {
      // Enough for 4up
      group = renderedPhotos.slice(i, i + 4); // End of slice not included
      i += 4;
    } else if (renderedPhotos[i] && renderedPhotos[i + 1]) {
      // Enough for 2up
      group = renderedPhotos.slice(i, i + 2);
      i += 2;
    } else {
      // This will be the last photo
      arrangedPhotos.push(renderedPhotos[i]);
      i++;
      continue;
    }

    arrangedPhotos.push(
      <TwoColumnContainer key={`two-column-${i}`} className="two-column-image-container">
        {group}
      </TwoColumnContainer>
    );
  }

  return arrangedPhotos;
};

export const getGalleryPhotos = (photos: MediaObject[], livePreviewUrl?: string, isMobile = false, displayEngagingImages = false, fieldsTouched = false): MediaObject[] => {
  const galleryPhotos = sortPhotos([...photos]);

  if (displayEngagingImages) {
    const engagingImage = getEngagingImage();
    galleryPhotos.unshift(engagingImage);
  }

  const shouldAddLivePreview = displayEngagingImages
    ? livePreviewUrl && fieldsTouched
    : !!livePreviewUrl;
  if (shouldAddLivePreview) {
    const livePreviewImage = generateLivePreviewImage(livePreviewUrl, !isMobile);
    if (isMobile) {
      // For mobile, live preview should always be in second position
      galleryPhotos.splice(LIVE_PREVIEW_MOBILE_INDEX, 0, livePreviewImage);
    } else {
      galleryPhotos.unshift(livePreviewImage);
    }
  }

  return galleryPhotos;
};

const getEngagingImage = (): MediaObject => {
  const url = 'https://au-prod-media.s3.us-west-2.amazonaws.com/media/epb-engaging-image.jpg';
  return {
    url,
    caption: '',
    full: url,
    img: url,
    isMain: true,
    position: '',
    thumb: url,
    type: 'image'
  };
};